/**
 * printInvoice(invoice)
 *
 * Opens a new browser window containing a print-ready invoice page and
 * immediately triggers window.print() so the user can save it as PDF
 * (or print it).
 *
 * `invoice` should be the full object returned by getInvoice() or
 * getInvoiceByShareToken() — it must include:
 *   invoice_number, issue_date, due_date, status, subtotal, tax_rate,
 *   tax_amount, discount_amount, total, notes
 *   firm: { name, email, phone, address }
 *   client: { first_name, last_name, organization, email, phone }
 *   line_items: [{ description, quantity, rate, amount }]
 */
// Small helper: escape user-supplied text before injecting into the printed
// HTML. Firm names, logos, addresses, etc. arrive from user input — failing
// to escape lets a maliciously-set firm name break the layout (or worse).
const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export function printInvoice(invoice) {
  const {
    firm = {}, client = {}, line_items = [],
    invoice_number, issue_date, due_date, status,
    subtotal = 0, tax_rate = 0, tax_amount = 0,
    discount_amount = 0, total = 0, notes = '',
    issuer_name,                             // snapshot of firm.name at issue time (migration 085)
  } = invoice;
  // Always show the name as-of issue time; fall back to live firm name then
  // the literal "Law Firm" for very old data.
  const displayFirmName = issuer_name || firm.name || 'Law Firm';

  const clientName = [client.first_name, client.last_name].filter(Boolean).join(' ')
    || client.organization || '—';

  const fmt = (n) =>
    'GH₵' + Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const statusColors = {
    draft:   '#94a3b8',
    sent:    '#3b82f6',
    viewed:  '#6366f1',
    partial: '#f59e0b',
    paid:    '#10b981',
    overdue: '#ef4444',
    void:    '#94a3b8',
  };
  const statusColor = statusColors[status] || '#94a3b8';

  const lineItemRows = line_items.map((li) => `
    <tr>
      <td style="padding:12px 0;border-bottom:1px solid #f1f5f9;">${escapeHtml(li.description || '')}</td>
      <td style="padding:12px 0 12px 24px;border-bottom:1px solid #f1f5f9;text-align:right;width:80px;">${escapeHtml(li.quantity)}</td>
      <td style="padding:12px 0 12px 24px;border-bottom:1px solid #f1f5f9;text-align:right;width:130px;">${fmt(li.rate)}</td>
      <td style="padding:12px 0 12px 24px;border-bottom:1px solid #f1f5f9;text-align:right;width:130px;font-weight:600;">${fmt(li.amount)}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Invoice ${escapeHtml(invoice_number || '')}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 13px; color: #1e293b; background: #fff; }
    @page { size: A4; margin: 12mm 14mm; }
    @media print { .no-print { display: none !important; } }

    .page { max-width: 780px; margin: 0 auto; padding: 32px; }

    /* Header */
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; gap: 24px; }
    .firm-block { display: flex; gap: 14px; align-items: center; }
    .firm-logo { max-height: 56px; max-width: 180px; width: auto; height: auto; object-fit: contain; }
    .firm-name { font-size: 22px; font-weight: 700; color: #1e293b; }
    .firm-meta { color: #64748b; font-size: 12px; margin-top: 6px; line-height: 1.7; }
    .inv-label { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #94a3b8; text-align: right; }
    .inv-number { font-size: 20px; font-weight: 700; text-align: right; margin-top: 2px; }
    .status-badge { display: inline-block; padding: 3px 10px; border-radius: 99px; font-size: 11px; font-weight: 600;
      color: #fff; background: ${statusColor}; margin-top: 6px; }

    /* Meta row */
    .meta-row { display: flex; gap: 0; border-top: 2px solid #4f46e5; border-bottom: 1px solid #e2e8f0; margin-bottom: 28px; }
    .meta-cell { flex: 1; padding: 12px 16px; border-right: 1px solid #e2e8f0; }
    .meta-cell:last-child { border-right: none; }
    .meta-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 4px; }
    .meta-value { font-size: 13px; font-weight: 600; color: #1e293b; }

    /* Table */
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    thead th { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8;
      padding: 0 0 10px; border-bottom: 2px solid #e2e8f0; font-weight: 600; }
    thead th:not(:first-child) { text-align: right; padding-left: 24px; }

    /* Totals */
    .totals { display: flex; justify-content: flex-end; }
    .totals-table { width: 260px; }
    .totals-row { display: flex; justify-content: space-between; padding: 5px 0; color: #64748b; font-size: 13px; }
    .totals-row.total { font-size: 15px; font-weight: 700; color: #1e293b;
      border-top: 2px solid #e2e8f0; padding-top: 10px; margin-top: 4px; }

    /* Notes */
    .notes { margin-top: 28px; padding: 14px 16px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
    .notes-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 6px; }
    .notes-text { color: #475569; line-height: 1.7; white-space: pre-wrap; }

    /* Footer */
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0;
      text-align: center; font-size: 11px; color: #94a3b8; }

    /* Download button — hidden when printing */
    .no-print {
      position: fixed; top: 16px; right: 16px; z-index: 999;
      background: #4f46e5; color: #fff; border: none; border-radius: 8px;
      padding: 10px 20px; font-size: 14px; font-weight: 600;
      cursor: pointer; display: flex; align-items: center; gap: 8px;
      box-shadow: 0 4px 12px rgba(79,70,229,0.4);
    }
    .no-print:hover { background: #4338ca; }
  </style>
</head>
<body>
<button class="no-print" onclick="window.print()">⬇ Download PDF</button>
<div class="page">

  <!-- Header -->
  <div class="header">
    <div class="firm-block">
      ${firm.logo_url
        ? `<img class="firm-logo" src="${escapeHtml(firm.logo_url)}" alt="${escapeHtml(firm.name || 'Firm logo')}" crossorigin="anonymous" />`
        : ''}
      <div>
        <div class="firm-name">${escapeHtml(displayFirmName)}</div>
        <div class="firm-meta">
          ${[firm.address, firm.email, firm.phone].filter(Boolean).map(escapeHtml).join('<br>')}
        </div>
      </div>
    </div>
    <div>
      <div class="inv-label">Invoice</div>
      <div class="inv-number">${escapeHtml(invoice_number || '')}</div>
      <div style="text-align:right;"><span class="status-badge">${escapeHtml((status || 'draft').toUpperCase())}</span></div>
    </div>
  </div>

  <!-- Meta row -->
  <div class="meta-row">
    <div class="meta-cell">
      <div class="meta-label">Billed to</div>
      <div class="meta-value">${escapeHtml(clientName)}</div>
      ${client.organization && clientName !== client.organization ? `<div style="color:#64748b;font-size:12px;">${escapeHtml(client.organization)}</div>` : ''}
      ${client.email ? `<div style="color:#64748b;font-size:12px;">${escapeHtml(client.email)}</div>` : ''}
    </div>
    <div class="meta-cell">
      <div class="meta-label">Issue Date</div>
      <div class="meta-value">${escapeHtml(fmtDate(issue_date))}</div>
    </div>
    <div class="meta-cell">
      <div class="meta-label">Due Date</div>
      <div class="meta-value">${escapeHtml(fmtDate(due_date))}</div>
    </div>
  </div>

  <!-- Line items -->
  <table>
    <thead>
      <tr>
        <th style="text-align:left;">Description</th>
        <th>Qty</th>
        <th>Rate</th>
        <th>Amount</th>
      </tr>
    </thead>
    <tbody>
      ${lineItemRows || `<tr><td colspan="4" style="padding:16px 0;color:#94a3b8;text-align:center;">No line items</td></tr>`}
    </tbody>
  </table>

  <!-- Totals -->
  <div class="totals">
    <div class="totals-table">
      <div class="totals-row"><span>Subtotal</span><span>${fmt(subtotal)}</span></div>
      ${Number(discount_amount) > 0 ? `<div class="totals-row"><span>Discount</span><span style="color:#10b981;">−${fmt(discount_amount)}</span></div>` : ''}
      ${Number(tax_amount) > 0 ? `<div class="totals-row"><span>Tax (${escapeHtml(tax_rate)}%)</span><span>${fmt(tax_amount)}</span></div>` : ''}
      <div class="totals-row total"><span>Total</span><span>${fmt(total)}</span></div>
    </div>
  </div>

  ${notes ? `<div class="notes"><div class="notes-label">Notes</div><div class="notes-text">${escapeHtml(notes)}</div></div>` : ''}

  <div class="footer">
    Thank you for your business — ${escapeHtml(displayFirmName)}
  </div>
</div>
<script>
  // Auto-open print dialog after a short delay so the page renders first
  window.addEventListener('load', () => setTimeout(() => window.print(), 600));
</script>
</body>
</html>`;

  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
  }
}
